import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { useCurrency } from '@/contexts/CurrencyContext'
import { formatDateHuman } from '@/lib/utils'
import { priceChangePct, sparklinePoints } from './purchasingLogic'
import { PURCHASING_STRINGS } from './purchasingStrings'

const S = PURCHASING_STRINGS.prices

/** Kitchen flow lane D: a sparkline of what we paid per stock unit, plus the
 *  last five buys with their vendor. */
export function PriceHistoryCard({ itemId, unit }: { itemId: string; unit: string }) {
  const { formatCurrency } = useCurrency()
  const { data } = useQuery({
    queryKey: ['priceHistory', itemId],
    queryFn: () => apiClient.getPriceHistory(itemId, 20).then((r) => r.data ?? []),
    // Always fetch fresh when shown: the app-wide 5-minute staleTime would
    // otherwise show a list from before the last receipt / count / issue.
    staleTime: 0,
  })
  const rows = data ?? [] // newest first
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border p-3">
        <h4 className="text-sm font-semibold">{S.title}</h4>
        <p className="text-xs text-muted-foreground">{S.none}</p>
      </div>
    )
  }
  const oldestFirst = [...rows].reverse().map((r) => r.unit_cost)
  const change = rows.length > 1 ? priceChangePct(rows[1].unit_cost, rows[0].unit_cost) : null
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-sm font-semibold">{S.title}</h4>
        {change != null && change !== 0 && (
          <span className={`text-xs font-medium tabular-nums ${change > 0 ? 'text-danger-fg' : 'text-success-fg'}`}>
            {change > 0 ? '▲' : '▼'} {Math.abs(change)}% {change > 0 ? S.up : S.down}
          </span>
        )}
      </div>
      <svg viewBox="0 0 240 40" className="my-2 h-10 w-full text-primary" role="img" aria-label={S.title}>
        <polyline fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" points={sparklinePoints(oldestFirst, 240, 40, 3)} />
      </svg>
      <p className="mb-1 text-xs text-muted-foreground">{S.last5}</p>
      <ul className="divide-y text-sm">
        {rows.slice(0, 5).map((r, i) => (
          <li key={`${r.received_at}-${i}`} className="flex items-baseline justify-between gap-2 py-1.5">
            <span className="min-w-0 truncate">
              {formatDateHuman(r.received_at)}
              {r.supplier_name ? <span className="text-muted-foreground"> · {r.supplier_name}</span> : null}
            </span>
            <span className="shrink-0 font-medium tabular-nums">
              {formatCurrency(r.unit_cost)} <span className="text-xs font-normal text-muted-foreground">{S.per} {unit}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
